import { html, LitElement, nothing, PropertyValues } from 'lit';
import { property, query, queryAll, queryAssignedElements, state } from 'lit/decorators.js';
import { ifDefined } from 'lit/directives/if-defined.js';
import { repeat } from 'lit/directives/repeat.js';
import { unsafeHTML } from 'lit/directives/unsafe-html.js';
import { unsafeSVG } from 'lit/directives/unsafe-svg.js';
import { guard } from 'lit/directives/guard.js';
import { alert, chevronDown } from '@triptease/icons';
import { styles } from './styles.js';
import { TtOption } from './tt-option/TtOption.js';

export class TtCombobox extends LitElement {
  static styles = styles;

  static formAssociated = true;

  static shadowRootOptions = {
    ...LitElement.shadowRootOptions,
    delegatesFocus: true,
  };

  @property({ type: Boolean })
  multiselect = false;

  @property({ type: Boolean })
  disabled = false;

  @property({ type: Boolean })
  invalid = false;

  @property({ type: Boolean, attribute: 'display-select-all' })
  displaySelectAll = false;

  @property({ type: Boolean })
  required = false;

  @property({ type: String })
  name = '';

  @property({ type: String, attribute: 'aria-labelledby' })
  ariaLabelledby = nothing;

  @property({ type: Boolean, attribute: 'hide-caret' })
  hideCaret = false;

  @property({ type: String })
  placeholder?: string;

  @property({ type: String, attribute: 'select-all-placeholder' })
  selectAllPlaceholder?: string;

  @property({ type: Boolean, attribute: 'open-upward' })
  openUpward = false;

  get form(): HTMLFormElement | null {
    return this.internals.form;
  }

  @state()
  protected _activeOption: number = -1;

  @state()
  protected _expanded = false;

  @state()
  protected _filter: string = '';

  @queryAssignedElements({ slot: 'option', selector: 'option' })
  options!: Array<HTMLOptionElement>;

  @queryAssignedElements({ slot: 'option', selector: 'tt-option:not([disabled])' })
  activeOptions!: Array<HTMLOptionElement>;

  @queryAssignedElements({ slot: 'error' })
  protected errorElements!: Array<HTMLElement>;

  @queryAll('tt-option:not([hidden], [disabled])')
  protected _visibleOptions!: Array<TtOption>;

  @queryAll('tt-option:not([hidden], .select-all)')
  protected _visibleOptionsNotSelectAll!: Array<TtOption>;

  @queryAll('tt-option:not([disabled], .select-all)')
  protected _selectableOptions!: NodeListOf<TtOption>;

  @queryAll('tt-option:not([disabled], [hidden], .select-all)')
  protected _selectableVisibleOptions!: NodeListOf<TtOption>;

  @query('input[role="combobox"]')
  protected _comboboxInput!: HTMLInputElement;

  @query('button:has(svg)')
  protected _chevronButton!: HTMLButtonElement;

  public internals: ReturnType<typeof this.attachInternals>;

  @property({ type: Array<string>, attribute: 'value' })
  public value: string[] = [];

  private get _isAllSelected(): boolean {
    return (
      Boolean(this._visibleOptionsNotSelectAll.length) &&
      Array.from(this._visibleOptionsNotSelectAll).every((option) => this.value.includes(option.value))
    );
  }

  protected get _selectedVisibleOptions(): Array<TtOption> {
    return Array.from(this._visibleOptions).filter((option) => this.value.includes(option.value));
  }

  protected get _selectedOptions(): Array<TtOption> {
    return Array.from(this._visibleOptionsNotSelectAll).filter((option) => this.value.includes(option.value));
  }

  private _slotObserver: MutationObserver = new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
      if (mutation.type === 'attributes') {
        this.requestUpdate('options');
      }
    });
  });

  constructor() {
    super();
    this.internals = this.attachInternals();
  }

  private _handleFocusOut(e: FocusEvent) {
    if (!this.shadowRoot!.contains(e.relatedTarget as Node | null)) {
      this._expanded = false;
    }
    this._filter = '';
  }

  private _onFocus() {
    this._comboboxInput.focus();
  }

  public connectedCallback() {
    super.connectedCallback();

    this.addEventListener('focus', this._onFocus);
  }

  public disconnectedCallback() {
    super.disconnectedCallback();

    this.removeEventListener('focus', this._onFocus);
  }

  protected firstUpdated(changedProperties: PropertyValues) {
    this.internals.setFormValue(JSON.stringify(this.value));

    this._reportValidity();
    this._listenForOptionChange();

    super.firstUpdated(changedProperties);
  }

  protected update(changedProperties: PropertyValues) {
    if (changedProperties.has('value')) {
      this._valueChanged();

      if (
        changedProperties.get('value') !== undefined &&
        JSON.stringify(changedProperties.get('value')) !== JSON.stringify(this.value)
      ) {
        this._dispatchSelectedOptions();
        this._reportValidity();
      }
    }

    if (changedProperties.has('_expanded') && changedProperties.get('_expanded') && !this._expanded) {
      this.internals.states.add('interacted');
    }

    if (changedProperties.has('options')) {
      this.updateComplete.then(() => {
        const selectedAssignedOptions = Array.from(this.options).filter((option) => option.selected);
        this.value = Array.from(new Set([...this.value, ...selectedAssignedOptions.map((option) => option.value!)]));
        this._listenForOptionChange();
      });
    }
    super.update(changedProperties);
  }

  private _valueChanged = () => {
    this._selectableOptions.forEach((option) => {
      if (this.value.includes(option.value!)) {
        this._checkOption(option);
      } else {
        this._uncheckOption(option);
      }
    });

    this.internals.setFormValue(JSON.stringify(this.value));
  };

  private _listenForOptionChange = () => {
    this.options.forEach((option) => {
      this._slotObserver.observe(option, { attributes: true, attributeFilter: ['selected', 'disabled', 'hidden'] });
    });
  };

  private _reportValidity() {
    if (this.required && !this.value.length) {
      const errorMessage = this.multiselect ? 'Please select at least one option' : 'Please select an option';

      this.internals.setValidity(
        {
          valueMissing: true,
        },
        errorMessage,
        this._comboboxInput
      );
      this.internals.states.add('--invalid');
    } else if (!this.internals.validity.valid) {
      this.internals.setValidity({});

      if (this.internals.states.has('--invalid')) {
        this.internals.states.delete('--invalid');
      }
    }
  }

  private get labels(): Array<HTMLElement> {
    return <Array<HTMLElement>>[...this.internals.labels];
  }

  private get labelContent(): string {
    if (this.ariaLabel !== null) {
      return this.ariaLabel;
    }

    return this.labels.map((label) => label.innerText).join(', ');
  }

  private _onKeyUp(event: KeyboardEvent) {
    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        if (!this._expanded) {
          this._expanded = true;
          this._activeOption = -1;
        } else {
          this._activeOption = Math.min(this._visibleOptions.length - 1, this._activeOption + 1);
        }
        break;
      case 'ArrowUp':
        event.preventDefault();
        if (this._expanded) {
          this._activeOption = Math.max(0, this._activeOption - 1);
        }
        break;
      case 'Escape':
        event.preventDefault();
        if (this._expanded) {
          this._expanded = false;
        } else if (this._filter !== '') {
          this._filter = '';
        }
        break;
      case 'Enter':
        event.preventDefault();
        if (this._expanded) {
          this._visibleOptions[this._activeOption].click();
        }
        break;
      default:
        break;
    }
  }

  private _uncheckOption(option: HTMLElement) {
    if (option.dataset.deselectable !== 'true') return;

    option.setAttribute('aria-selected', 'false');
    const checkbox = <HTMLInputElement>option.querySelector('input[type="checkbox"]');
    if (checkbox) {
      checkbox.checked = false;
    }
  }

  private _checkOption(option: HTMLElement) {
    if (!this.multiselect) {
      this._selectableOptions.forEach((opt) => {
        this._uncheckOption(opt);
      });
    }

    option.setAttribute('aria-selected', 'true');
    const checkbox = <HTMLInputElement>option.querySelector('input[type="checkbox"]');
    if (checkbox) {
      checkbox.checked = true;
    }
  }

  private _getActiveOptionId(): string | undefined {
    if (this._activeOption === -1 || this._visibleOptions.length === 0) {
      return undefined;
    }

    const option = this._visibleOptions[this._activeOption];
    return option.id;
  }

  private _onClick() {
    this._expanded = !this._expanded;

    if (this._expanded) {
      (<HTMLElement>this.shadowRoot?.querySelector('input[role="combobox"]'))?.focus();
    }
  }

  private _onChevronClick() {
    (<HTMLElement>this.shadowRoot?.querySelector('input[role="combobox"]'))?.focus();
  }

  private _onClickOption(event: MouseEvent) {
    event.preventDefault();
    event.stopPropagation();

    const option = event.currentTarget as TtOption;
    if (option.disabled) return;

    const selectedValue = option.value as string;

    if (this.multiselect) {
      if (this.value.includes(selectedValue)) {
        this.value = this.value.filter((value) => value !== selectedValue);
      } else {
        this.value = [...this.value, selectedValue];
      }
    } else {
      this.value = [selectedValue];
      this._expanded = false;
    }
  }

  private _onInput(event: Event) {
    const input = event.target as HTMLInputElement;
    const filter = input.value;
    if (filter !== '' && !this._expanded) {
      this._expanded = true;
    }
    this._activeOption = -1;
    this._filter = filter;
  }

  private _dispatchSelectedOptions() {
    this.dispatchEvent(
      new Event('change', {
        bubbles: true,
        composed: true,
      })
    );
  }

  private _selectAll = (event: MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();

    // If filtering, and all visible options are selected, deselect only visible options
    // Else if filtering, select all visible options

    if (this._filter !== '' && this._isAllSelected) {
      const selectedVisibleValues = this._selectedVisibleOptions.map((option) => option.value);
      this.value = this.value.filter((value) => !selectedVisibleValues.includes(value));
    } else if (this._filter !== '') {
      this.value = Array.from(
        new Set([...this.value, ...Array.from(this._selectableVisibleOptions).map((option) => option.value)])
      );
    } else if (this.value.length === this._selectableOptions.length) {
      this.value = [];
    } else {
      this.value = Array.from(this._selectableOptions).map((option) => option.value);
    }
  };

  private get _isValid(): boolean {
    return this.internals.validity.valid && !this.invalid;
  }

  private _renderCombobox() {
    const placeHolderValue = guard(
      [this.placeholder, this.disabled, this.value, this.activeOptions.length, this.selectAllPlaceholder, this._filter],
      () => {
        if (this.placeholder) {
          return this.placeholder;
        }

        if (this.disabled) {
          return 'Disabled';
        }
        if (!this.value.length) {
          return 'No options selected';
        }
        if (this.value.length === 1) {
          return Array.from(this._selectableVisibleOptions)
            .find((option) => option.value === this.value[0])
            ?.textContent?.trim();
        }

        // Count all selected values, excluding only those that correspond to permanently hidden options
        const permanentlyHiddenValues = Array.from(this.options)
          .filter((option) => option.hidden)
          .map((option) => option.value);

        const selectedCount = this.value.filter((val) => !permanentlyHiddenValues.includes(val)).length;

        // Only show "All options selected" when truly all non-hidden options are selected
        const totalSelectableCount = this.options.length - permanentlyHiddenValues.length;
        if (this._filter === '' && selectedCount === totalSelectableCount && totalSelectableCount > 0) {
          return this.selectAllPlaceholder || 'All options selected';
        }

        return `${selectedCount} options selected`;
      }
    );

    return html` <input
      type="text"
      id="${this.id}"
      name="${this.name}"
      autocomplete="off"
      aria-label="${this.labelContent}"
      aria-labelledby="${this.ariaLabelledby}"
      role="combobox"
      aria-controls="${this.id}-list"
      aria-expanded="${this._expanded}"
      aria-autocomplete="list"
      aria-haspopup="listbox"
      aria-disabled="${this.disabled}"
      aria-invalid="${!this._isValid}"
      aria-errormessage="${!this._isValid ? `error-msg-${this.id}` : nothing}"
      aria-required="${this.required}"
      ?disabled=${this.disabled}
      aria-activedescendant="${ifDefined(this._getActiveOptionId())}"
      @input="${this._onInput}"
      .value="${this._filter}"
      .placeholder="${placeHolderValue}"
      @focusout="${this._handleFocusOut}"
      part="input"
      class=${this.hideCaret ? 'hide-caret' : ''}
    />`;
  }

  private _renderSelectAll = () => {
    if (!this.multiselect || !this.displaySelectAll) {
      return nothing;
    }
    const id = `${this.id}-option-select-all`;
    const active = this._getActiveOptionId() === id;
    const selected = this._isAllSelected;

    return html`
      <tt-option
        id="${id}"
        class="select-all"
        ?selected=${selected}
        ?active=${active}
        include-checkbox
        @click="${this._selectAll}"
        exportparts="option, checkbox"
        part="option"
        value="select-all"
        >Select all</tt-option
      >
    `;
  };

  private _renderOption(option: HTMLOptionElement) {
    const hidden = Boolean(
      (this._filter !== '' &&
        !option.value.toLowerCase().includes(this._filter.toLowerCase()) &&
        !option.innerText.toLowerCase().includes(this._filter.toLowerCase())) ||
        option.hidden
    );

    const id = `${this.id}-option-${option.value}`;
    const active = this._getActiveOptionId() === id;
    const selected = this.value.includes(option.value);

    return html`
      <tt-option
        id="${id}"
        value="${option.value}"
        ?selected=${selected}
        ?disabled=${option.disabled}
        ?active=${active}
        ?include-checkbox=${this.multiselect}
        ?hidden=${hidden}
        @click="${this._onClickOption}"
      >
        ${unsafeHTML(option.innerHTML)}
      </tt-option>
    `;
  }

  private _renderChevron = () => html`
    <button
      type="button"
      aria-label="Expand"
      @click="${this._onChevronClick}"
      aria-expanded="${this._expanded}"
      aria-controls="${this.id}-list"
      tabindex="-1"
      ?disabled=${this.disabled}
      part="arrow"
    >
      ${unsafeSVG(chevronDown)}
    </button>
  `;

  private _hasErrorContent(): boolean {
    return this.errorElements?.length > 0;
  }

  render() {
    return html`
      <div
        class="tt-combobox-container"
        @focus="${this._onFocus}"
        @keydown="${this._onKeyUp}"
        @click="${this._onClick}"
        aria-disabled="${this.disabled}"
        part="container"
      >
        <slot name="icon" part="icon"></slot>
        ${this._renderCombobox()} ${this._renderChevron()}
        <ul
          id="${this.id}-list"
          role="listbox"
          aria-multiselectable="${this.multiselect}"
          aria-label="${this.labelContent}"
          part="listbox"
          ?data-open-upward="${this.openUpward}"
        >
          ${this._renderSelectAll()} ${repeat(this.options, (opt) => opt.value, this._renderOption.bind(this))}

          <li part="no-results" class="no-results">No results</li>
        </ul>
      </div>
      <slot name="option" @slotchange=${() => this.requestUpdate('options')}></slot>
      <div
        class="errormessage"
        id="error-msg-${this.id}"
        role="alert"
        aria-atomic="true"
        ?data-hidden="${!this._hasErrorContent()}"
        part="error"
      >
        ${unsafeSVG(alert)}
        <slot name="error"></slot>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'tt-combobox': TtCombobox;
  }
}
